import React, { useEffect, useState, useRef } from 'react';
import {
  Box,
  Paper,
  Typography,
  TextField,
  FormControlLabel,
  Checkbox,
  Button,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormGroup,
  CircularProgress,
  Alert,
  Autocomplete,
  Chip,
} from '@mui/material';
import { dispositionOptions } from '../constants/dispositions';
import {
  CASE_STATUS_NOTIFICATION_OPTIONS,
  DEFAULT_CASE_STATUS_NOTIFICATION_STATUSES,
} from '../constants/caseStatusNotifications';
import { createFieldChangeHandler, getFieldError, hasFieldError } from '../utils/formErrorHandling';
import { projectAPI, firmAPI } from '../services/api';
// @ts-ignore
import { CKEditor } from '@ckeditor/ckeditor5-react';
// @ts-ignore
import ClassicEditor from '@ckeditor/ckeditor5-build-classic';

const ClassicEditorAny: any = ClassicEditor;

export type AppendixRateRange = {
  from: string;
  to: string;
  rate: string;
};

export type ProjectFormValues = {
  name: string;
  description: string;
  enable_checklist: boolean;
  checklist_instructions: string;
  checklist_files?: File[];
  billing_type: '' | 'Page Based' | 'Case Based' | 'Time Based' | 'Flat Rate' | 'Consolidation';
  rate: string;
  rush_rate?: string;
  flat_rate?: string;
  flat_rate_page_from?: string;
  flat_rate_page_to?: string;
  variable_rate?: string;
  variable_rate_page_from?: string;
  variable_rate_page_to?: string;
  additional_claimant?: string;
  appendix_c_rate?: string;
  appendix_c_rate_ranges?: AppendixRateRange[];
  free_cases_allowed: string;
  hide_case_timer: boolean;
  enable_estimate_approval: boolean;
  max_estimate_hours: string;
  enable_sample_estimate: boolean;
  max_capacity_pages_per_hour: string;
  remind_on_file_missing: boolean;
  due_date_required: boolean;
  priority_based_due_date: boolean;
  notify_admins_on_case_status_change: boolean;
  case_status_change_notification_statuses: string[];
  enable_disposition: boolean;
  auto_assign_vendors: boolean;
  auto_clone_vendor_firm_id: number | '';
  auto_clone_vendor_user_id: number | '';
  mandatory_disposition_field: boolean;
  disposition_list: string[];
  status: 'active' | 'draft';
};

type Props = {
  initialValues: ProjectFormValues;
  submitting?: boolean;
  mode: 'create' | 'edit';
  onSubmit: (values: ProjectFormValues, overrideStatus?: 'active' | 'draft') => void;
  onCancel: () => void;
  enableFileUpload?: boolean;
  showStatusSelect?: boolean;
  fieldErrors?: Record<string, string[]>;
  fieldRefs?: React.MutableRefObject<Record<string, HTMLElement | null>>;
  projectId?: number;
};

const ProjectForm: React.FC<Props> = ({
  initialValues,
  submitting = false,
  mode,
  onSubmit,
  onCancel,
  projectId,
  enableFileUpload = false,
  showStatusSelect = false,
  fieldErrors = {},
  fieldRefs,
}) => {
  const [form, setForm] = useState<ProjectFormValues>(initialValues);
  const priorityDueDateTouched = useRef(false);
  const [projectActivities, setProjectActivities] = useState<any[]>([]);
  const [loadingActivities, setLoadingActivities] = useState(false);
  const [vendorFirms, setVendorFirms] = useState<any[]>([]);
  const [vendorUsers, setVendorUsers] = useState<any[]>([]);

  // Create field change handler that clears errors
  const handleFieldChange = createFieldChangeHandler(setForm, () => {});

  useEffect(() => {
    setForm(initialValues);
  }, [initialValues]);

  useEffect(() => {
    const loadVendors = async () => {
      try {
        const res = await firmAPI.getAll({ type: 'vendor', status: 'approved', per_page: 1000 });
        const vendors = (res.data.data?.data || res.data.data || []).filter((f: any) => f.type === 'vendor');
        setVendorFirms(vendors);
      } catch {
        setVendorFirms([]);
      }
    };
    loadVendors();
  }, []);

  useEffect(() => {
    if (!form.auto_clone_vendor_firm_id) {
      setVendorUsers([]);
      return;
    }
    (async () => {
      try {
        const res = await firmAPI.members(Number(form.auto_clone_vendor_firm_id));
        const list = (res.data.data || []).map((u: any) => ({ id: u.id, name: `${u.name} (${u.email})` }));
        setVendorUsers(list);
      } catch {
        setVendorUsers([]);
      }
    })();
  }, [form.auto_clone_vendor_firm_id]);

  // Load activities when project is time-based and we have a projectId (edit mode)
  useEffect(() => {
    if (form.billing_type === 'Time Based' && projectId && mode === 'edit') {
      loadActivities();
    } else {
      setProjectActivities([]);
    }
  }, [form.billing_type, projectId, mode]);

  const loadActivities = async () => {
    if (!projectId) return;
    try {
      setLoadingActivities(true);
      const response = await projectAPI.getActivities(projectId);
      setProjectActivities(response.data.data || []);
    } catch (err) {
      console.error('Failed to load activities:', err);
      setProjectActivities([]);
    } finally {
      setLoadingActivities(false);
    }
  };

  const handleFileChange = (files: FileList | null) => {
    if (!files) return;
    const arr = Array.from(files);
    setForm((prev) => ({ ...prev, checklist_files: arr }));
  };

  return (
    <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 2 }}>
      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Basic Information</Typography>
        <TextField
          label="Title *"
          placeholder="Title"
          value={form.name}
          onChange={(e) => {
            const name = e.target.value;
            handleFieldChange('name', name);
            if (!priorityDueDateTouched.current && name.trim().toLowerCase() === 'demand letter') {
              setForm((prev) => ({ ...prev, priority_based_due_date: true }));
            }
          }}
          error={hasFieldError(fieldErrors, 'name')}
          helperText={getFieldError(fieldErrors, 'name')}
          inputRef={(el) => fieldRefs && (fieldRefs.current.name = el)}
          fullWidth
        />
        <Box>
          <Typography variant="body2" color="text.secondary">Body</Typography>
          <Box 
            sx={{ 
              border: hasFieldError(fieldErrors, 'description') ? '2px solid #d32f2f' : '1px solid #ccc',
              borderRadius: 1,
              '& .ck-editor__editable': {
                borderColor: hasFieldError(fieldErrors, 'description') ? '#d32f2f !important' : undefined,
              }
            }}
            ref={(el: HTMLDivElement | null) => {
              if (fieldRefs) fieldRefs.current.description = el;
            }}
          >
            <CKEditor
              editor={ClassicEditorAny}
              data={form.description}
              onChange={(_event: unknown, editor: any) => {
                const data = editor.getData();
                handleFieldChange('description', data);
              }}
            />
          </Box>
          {hasFieldError(fieldErrors, 'description') && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5, display: 'block' }}>
              {getFieldError(fieldErrors, 'description')}
            </Typography>
          )}
        </Box>
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Checklist Settings</Typography>
        <FormControlLabel
          control={<Checkbox checked={form.enable_checklist} onChange={(e) => setForm((p) => ({ ...p, enable_checklist: e.target.checked }))} />}
          label="Enable Checklist"
        />
        {form.enable_checklist && (
          <>
            <Typography variant="body2" color="text.secondary">Checklist Instructions</Typography>
            <CKEditor
              editor={ClassicEditorAny}
              data={form.checklist_instructions}
              onChange={(_event: unknown, editor: any) => setForm((p) => ({ ...p, checklist_instructions: editor.getData() }))}
            />

            {enableFileUpload && (
              <>
                <Button component="label" variant="outlined">
                  Upload Checklist Files
                  <input hidden multiple type="file" accept=".pdf,.docx,.xlsx,.jpeg,.jpg,.png" onChange={(e) => handleFileChange(e.target.files)} />
                </Button>
                {!!form.checklist_files?.length && (
                  <Typography variant="caption" color="text.secondary">
                    {form.checklist_files.length} file(s) selected
                  </Typography>
                )}
              </>
            )}
          </>
        )}
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Project Settings</Typography>
        <FormControl 
          fullWidth 
          error={hasFieldError(fieldErrors, 'billing_type')}
          ref={(el: HTMLDivElement | null) => {
            if (fieldRefs) fieldRefs.current.billing_type = el;
          }}
        >
          <InputLabel>Billing Type *</InputLabel>
          <Select
            label="Billing Type *"
            value={form.billing_type}
            onChange={(e) => handleFieldChange('billing_type', e.target.value)}
          >
            <MenuItem value="">--Choose--</MenuItem>
            <MenuItem value="Page Based">Page Based</MenuItem>
            <MenuItem value="Consolidation">Consolidation</MenuItem>
            <MenuItem value="Case Based">Case Based</MenuItem>
            <MenuItem value="Time Based">Time Based</MenuItem>
            <MenuItem value="Flat Rate">Flat Rate</MenuItem>
          </Select>
          {hasFieldError(fieldErrors, 'billing_type') && (
            <Typography variant="caption" color="error" sx={{ mt: 0.5 }}>
              {getFieldError(fieldErrors, 'billing_type')}
            </Typography>
          )}
        </FormControl>
        
        {/* Regular Rate field - shown for all billing types except Flat Rate */}
        {form.billing_type !== 'Flat Rate' && (
          <TextField 
            label="Rate *" 
            value={form.rate} 
            onChange={(e) => handleFieldChange('rate', e.target.value)}
            error={hasFieldError(fieldErrors, 'rate')}
            helperText={getFieldError(fieldErrors, 'rate')}
            inputRef={(el) => fieldRefs && (fieldRefs.current.rate = el)}
            fullWidth 
          />
        )}

        {form.billing_type === 'Page Based' && (
          <TextField
            label="RUSH Rate"
            type="number"
            value={form.rush_rate ?? ''}
            onChange={(e) => handleFieldChange('rush_rate', e.target.value)}
            error={hasFieldError(fieldErrors, 'rush_rate')}
            helperText={getFieldError(fieldErrors, 'rush_rate') || 'Optional. Per-page rate used instead of Rate when the case Rush checkbox is checked. Leave empty to always use Rate.'}
            inputProps={{ min: 0, step: 'any' }}
            fullWidth
          />
        )}

        {form.billing_type === 'Page Based' && (
          <Box sx={{ mt: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 600 }}>
              Appendix Rate Ranges
            </Typography>
            <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mb: 1 }}>
              Optional. Define tiered rates for appendix C based on the total page count (not total minus reviewed). Leave empty to disable appendix billing.
            </Typography>
            {(form.appendix_c_rate_ranges || []).map((range, index) => (
              <Box key={index} sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr 1fr auto', gap: 1, mb: 1, alignItems: 'center' }}>
                <TextField
                  label="Page From"
                  value={range.from}
                  onChange={(e) => {
                    const ranges = [...(form.appendix_c_rate_ranges || [])];
                    ranges[index] = { ...ranges[index], from: e.target.value };
                    setForm((p) => ({ ...p, appendix_c_rate_ranges: ranges }));
                  }}
                  size="small"
                  placeholder="1"
                />
                <TextField
                  label="Page To"
                  value={range.to}
                  onChange={(e) => {
                    const ranges = [...(form.appendix_c_rate_ranges || [])];
                    ranges[index] = { ...ranges[index], to: e.target.value };
                    setForm((p) => ({ ...p, appendix_c_rate_ranges: ranges }));
                  }}
                  size="small"
                  placeholder="1000 or empty"
                  helperText="Empty = unlimited"
                />
                <TextField
                  label="Rate"
                  value={range.rate}
                  onChange={(e) => {
                    const ranges = [...(form.appendix_c_rate_ranges || [])];
                    ranges[index] = { ...ranges[index], rate: e.target.value };
                    setForm((p) => ({ ...p, appendix_c_rate_ranges: ranges }));
                  }}
                  size="small"
                  placeholder="0.10"
                />
                <Button
                  color="error"
                  size="small"
                  onClick={() => {
                    const ranges = [...(form.appendix_c_rate_ranges || [])];
                    ranges.splice(index, 1);
                    setForm((p) => ({ ...p, appendix_c_rate_ranges: ranges }));
                  }}
                >
                  Remove
                </Button>
              </Box>
            ))}
            <Button
              variant="outlined"
              size="small"
              onClick={() => {
                const ranges = [...(form.appendix_c_rate_ranges || [])];
                const lastTo = ranges.length > 0 ? ranges[ranges.length - 1].to : '';
                const nextFrom = lastTo ? String(Number(lastTo) + 1) : '1';
                ranges.push({ from: nextFrom, to: '', rate: '' });
                setForm((p) => ({ ...p, appendix_c_rate_ranges: ranges }));
              }}
            >
              Add Range
            </Button>
          </Box>
        )}

        {form.billing_type === 'Consolidation' && (
          <TextField
            label="Appendix Rate"
            value={form.appendix_c_rate || ''}
            onChange={(e) => handleFieldChange('appendix_c_rate', e.target.value)}
            error={hasFieldError(fieldErrors, 'appendix_c_rate')}
            helperText={getFieldError(fieldErrors, 'appendix_c_rate') || 'Flat rate per page for appendix C billing'}
            inputRef={(el) => fieldRefs && (fieldRefs.current.appendix_c_rate = el)}
            fullWidth
            placeholder="e.g. 0.05"
          />
        )}

        {/* Billable Activities section - shown only for Time Based projects */}
        {form.billing_type === 'Time Based' && (
          <Box sx={{ mt: 2, p: 2, bgcolor: 'background.default', borderRadius: 1 }}>
            <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
              Billable Activities
            </Typography>
            {mode === 'create' ? (
              <Alert severity="info" sx={{ mt: 1 }}>
                After creating the project, you can select which activities are billable from the project view page.
                Only billable activities can be selected when stopping timers to track billable time.
              </Alert>
            ) : (
              <>
                {loadingActivities ? (
                  <Box display="flex" justifyContent="center" p={2}>
                    <CircularProgress size={24} />
                  </Box>
                ) : projectActivities.length > 0 ? (
                  <Box sx={{ mt: 1 }}>
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
                      {projectActivities.filter((a: any) => a.is_billable).length} billable {projectActivities.filter((a: any) => a.is_billable).length === 1 ? 'activity' : 'activities'} selected
                    </Typography>
                    <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                      {projectActivities
                        .filter((a: any) => a.is_billable)
                        .map((activity: any) => (
                          <Chip
                            key={activity.id}
                            label={activity.name}
                            size="small"
                            color="primary"
                            variant="outlined"
                          />
                        ))}
                    </Box>
                    <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                      Manage billable activities from the project view page after saving.
                    </Typography>
                  </Box>
                ) : (
                  <Alert severity="info" sx={{ mt: 1 }}>
                    No billable activities selected. Select activities from the project view page after saving.
                  </Alert>
                )}
              </>
            )}
          </Box>
        )}
        
        {/* Flat Rate fields - shown only when Flat Rate is selected */}
        {form.billing_type === 'Flat Rate' && (
          <>
            <TextField 
              label="Flat Rate *" 
              value={form.flat_rate || ''} 
              onChange={(e) => handleFieldChange('flat_rate', e.target.value)}
              error={hasFieldError(fieldErrors, 'flat_rate')}
              helperText={getFieldError(fieldErrors, 'flat_rate')}
              inputRef={(el) => fieldRefs && (fieldRefs.current.flat_rate = el)}
              fullWidth 
              placeholder="Enter flat rate amount"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                label="Flat Rate Page From *" 
                value={form.flat_rate_page_from || ''} 
                onChange={(e) => handleFieldChange('flat_rate_page_from', e.target.value)}
                error={hasFieldError(fieldErrors, 'flat_rate_page_from')}
                helperText={getFieldError(fieldErrors, 'flat_rate_page_from')}
                inputRef={(el) => fieldRefs && (fieldRefs.current.flat_rate_page_from = el)}
                fullWidth 
                placeholder="0"
              />
              <TextField 
                label="Flat Rate Page To *" 
                value={form.flat_rate_page_to || ''} 
                onChange={(e) => handleFieldChange('flat_rate_page_to', e.target.value)}
                error={hasFieldError(fieldErrors, 'flat_rate_page_to')}
                helperText={getFieldError(fieldErrors, 'flat_rate_page_to')}
                inputRef={(el) => fieldRefs && (fieldRefs.current.flat_rate_page_to = el)}
                fullWidth 
                placeholder="e.g., 100"
              />
            </Box>
            <TextField 
              label="Variable Rate *" 
              value={form.variable_rate || ''} 
              onChange={(e) => handleFieldChange('variable_rate', e.target.value)}
              error={hasFieldError(fieldErrors, 'variable_rate')}
              helperText={getFieldError(fieldErrors, 'variable_rate')}
              inputRef={(el) => fieldRefs && (fieldRefs.current.variable_rate = el)}
              fullWidth 
              placeholder="Enter variable rate amount"
            />
            <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2 }}>
              <TextField 
                label="Variable Rate Page From *" 
                value={form.variable_rate_page_from || ''} 
                onChange={(e) => handleFieldChange('variable_rate_page_from', e.target.value)}
                error={hasFieldError(fieldErrors, 'variable_rate_page_from')}
                helperText={getFieldError(fieldErrors, 'variable_rate_page_from')}
                inputRef={(el) => fieldRefs && (fieldRefs.current.variable_rate_page_from = el)}
                fullWidth 
                placeholder="e.g., 101"
              />
              <TextField 
                label="Variable Rate Page To (Optional)" 
                value={form.variable_rate_page_to || ''} 
                onChange={(e) => handleFieldChange('variable_rate_page_to', e.target.value)}
                error={hasFieldError(fieldErrors, 'variable_rate_page_to')}
                helperText={getFieldError(fieldErrors, 'variable_rate_page_to')}
                inputRef={(el) => fieldRefs && (fieldRefs.current.variable_rate_page_to = el)}
                fullWidth 
                placeholder="Leave empty for unlimited"
              />
            </Box>
            <TextField 
              label="Additional Claimant (Optional)" 
              value={form.additional_claimant ?? ''} 
              onChange={(e) => handleFieldChange('additional_claimant', e.target.value)}
              error={hasFieldError(fieldErrors, 'additional_claimant')}
              helperText="Single value added to estimate when 'Additional Claimant' is checked in client estimate"
              inputRef={(el) => fieldRefs && (fieldRefs.current.additional_claimant = el)}
              fullWidth 
              placeholder="e.g., 30"
            />
          </>
        )}
        
        <TextField 
          label="No of Free cases allowed *" 
          value={form.free_cases_allowed} 
          onChange={(e) => handleFieldChange('free_cases_allowed', e.target.value)}
          error={hasFieldError(fieldErrors, 'free_cases_allowed')}
          helperText={getFieldError(fieldErrors, 'free_cases_allowed')}
          inputRef={(el) => fieldRefs && (fieldRefs.current.free_cases_allowed = el)}
          fullWidth 
        />
        <FormControlLabel control={<Checkbox checked={form.hide_case_timer} onChange={(e) => setForm((p) => ({ ...p, hide_case_timer: e.target.checked }))} />} label="Hide Case Timer" />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.auto_assign_vendors}
              onChange={(e) => setForm((p) => ({
                ...p,
                auto_assign_vendors: e.target.checked,
                auto_clone_vendor_firm_id: e.target.checked ? p.auto_clone_vendor_firm_id : '',
                auto_clone_vendor_user_id: e.target.checked ? p.auto_clone_vendor_user_id : '',
              }))}
            />
          }
          label="Auto-clone client cases to a vendor firm"
        />
        <Typography variant="caption" color="text.secondary">
          When enabled, cases created by client users in this project are cloned to the selected vendor using the same assignment flow as case creation.
        </Typography>
        {form.auto_assign_vendors && (
          <>
            <Autocomplete
              options={vendorFirms}
              getOptionLabel={(o) => o?.name || ''}
              value={vendorFirms.find((v) => v.id === form.auto_clone_vendor_firm_id) || null}
              onChange={(_e, v: any) => setForm((p) => ({
                ...p,
                auto_clone_vendor_firm_id: v?.id || '',
                auto_clone_vendor_user_id: '',
              }))}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Vendor firm *"
                  error={hasFieldError(fieldErrors, 'auto_clone_vendor_firm_id')}
                  helperText={getFieldError(fieldErrors, 'auto_clone_vendor_firm_id') || 'Required when auto-clone is enabled'}
                />
              )}
            />
            <Autocomplete
              options={vendorUsers}
              getOptionLabel={(o) => o?.name || ''}
              value={vendorUsers.find((u) => u.id === form.auto_clone_vendor_user_id) || null}
              onChange={(_e, v: any) => setForm((p) => ({ ...p, auto_clone_vendor_user_id: v?.id || '' }))}
              renderInput={(params) => (
                <TextField {...params} label="Vendor user (optional)" helperText="If set, the cloned vendor case is assigned to this user" />
              )}
            />
          </>
        )}
        <Typography variant="body2" sx={{ fontWeight: 600 }}>
          Case status change notifications
        </Typography>
        <Typography variant="caption" color="text.secondary">
          Send emails when a case moves to the selected statuses. Recipients include the case creator and firm managers.
        </Typography>
        <FormGroup sx={{ pl: 1 }}>
          {CASE_STATUS_NOTIFICATION_OPTIONS.map((option) => (
            <FormControlLabel
              key={option.value}
              control={
                <Checkbox
                  checked={form.case_status_change_notification_statuses.includes(option.value)}
                  onChange={(e) => {
                    const checked = e.target.checked;
                    setForm((p) => ({
                      ...p,
                      case_status_change_notification_statuses: checked
                        ? [...p.case_status_change_notification_statuses, option.value]
                        : p.case_status_change_notification_statuses.filter((status) => status !== option.value),
                    }));
                  }}
                />
              }
              label={option.label}
            />
          ))}
        </FormGroup>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.notify_admins_on_case_status_change}
              onChange={(e) => setForm((p) => ({ ...p, notify_admins_on_case_status_change: e.target.checked }))}
            />
          }
          label="Also notify Email Settings admins"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          When enabled, recipients from Email Settings → Case Notifications (PMP/Site admins) are also included for the selected statuses above.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Estimate Settings</Typography>
        <FormControlLabel control={<Checkbox checked={form.enable_estimate_approval} onChange={(e) => setForm((p) => ({ ...p, enable_estimate_approval: e.target.checked }))} />} label="Enable estimate approval" />
        <TextField 
          label="Max Estimate (Hours)" 
          value={form.max_estimate_hours} 
          onChange={(e) => handleFieldChange('max_estimate_hours', e.target.value)}
          error={hasFieldError(fieldErrors, 'max_estimate_hours')}
          helperText={getFieldError(fieldErrors, 'max_estimate_hours')}
          inputRef={(el) => fieldRefs && (fieldRefs.current.max_estimate_hours = el)}
          fullWidth 
        />
        <FormControlLabel control={<Checkbox checked={form.enable_sample_estimate} onChange={(e) => setForm((p) => ({ ...p, enable_sample_estimate: e.target.checked }))} />} label="Enable Sample Estimate" />
        {form.enable_sample_estimate && (
          <TextField 
            label="Max Capacity (Pages/Hour)" 
            value={form.max_capacity_pages_per_hour} 
            onChange={(e) => handleFieldChange('max_capacity_pages_per_hour', e.target.value)}
            error={hasFieldError(fieldErrors, 'max_capacity_pages_per_hour')}
            helperText={getFieldError(fieldErrors, 'max_capacity_pages_per_hour')}
            inputRef={(el) => fieldRefs && (fieldRefs.current.max_capacity_pages_per_hour = el)}
            fullWidth 
          />
        )}
        <FormControlLabel control={<Checkbox checked={form.remind_on_file_missing} onChange={(e) => setForm((p) => ({ ...p, remind_on_file_missing: e.target.checked }))} />} label="Remind on File Missing" />
        <FormControlLabel
          control={
            <Checkbox
              checked={form.due_date_required}
              onChange={(e) => setForm((p) => ({ ...p, due_date_required: e.target.checked }))}
            />
          }
          label="Due date required on cases"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          When enabled (default), every case in this project must have a due date. Disable only if due dates are optional for this project.
        </Typography>
        <FormControlLabel
          control={
            <Checkbox
              checked={form.priority_based_due_date}
              onChange={(e) => {
                priorityDueDateTouched.current = true;
                setForm((p) => ({ ...p, priority_based_due_date: e.target.checked }));
              }}
            />
          }
          label="Set due date from priority"
        />
        <Typography variant="caption" color="text.secondary" sx={{ mt: -1 }}>
          When enabled, clients cannot choose a due date. High priority is due in 3 working days, Normal in 5, and Low in 7. Weekends are skipped. A requested delivery date can be added in the case description. Demand Letter projects have this on by default.
        </Typography>
      </Paper>

      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Disposition Settings</Typography>
        <FormControlLabel control={<Checkbox checked={form.enable_disposition} onChange={(e) => setForm((p) => ({ ...p, enable_disposition: e.target.checked }))} />} label="Enable Disposition" />
        {form.enable_disposition && (
          <>
            <FormControlLabel control={<Checkbox checked={form.mandatory_disposition_field} onChange={(e) => setForm((p) => ({ ...p, mandatory_disposition_field: e.target.checked }))} />} label="Mandatory Disposition Field" />
            <FormGroup>
              {dispositionOptions.map((opt) => (
                <FormControlLabel
                  key={opt}
                  control={
                    <Checkbox
                      checked={form.disposition_list.includes(opt)}
                      onChange={(e) => {
                        const checked = e.target.checked;
                        setForm((p) => ({
                          ...p,
                          disposition_list: checked ? [...p.disposition_list, opt] : p.disposition_list.filter((o) => o !== opt),
                        }));
                      }}
                    />
                  }
                  label={opt}
                />
              ))}
            </FormGroup>
          </>
        )}
      </Paper>

      {showStatusSelect && (
        <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2 }}>
          <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Status</Typography>
          <FormControl fullWidth>
            <InputLabel>Status</InputLabel>
            <Select
              label="Status"
              value={form.status}
              onChange={(e) => setForm((p) => ({ ...p, status: e.target.value as 'active' | 'draft' }))}
            >
              <MenuItem value="active">Active</MenuItem>
              <MenuItem value="draft">Draft</MenuItem>
            </Select>
          </FormControl>
        </Paper>
      )}

      <Paper sx={{ p: 3, display: 'flex', flexDirection: 'column', gap: 2, gridColumn: { md: '1 / -1' } }}>
        <Typography variant="subtitle1" sx={{ color: 'primary.main' }}>Actions</Typography>
        <Box sx={{ display: 'flex', gap: 2, mt: 1 }}>
          <Button variant="outlined" onClick={onCancel}>Cancel</Button>
          {mode === 'create' ? (
            <>
              <Button
                variant="contained"
                onClick={() => onSubmit(form, 'active')}
                disabled={submitting}
              >
                {submitting ? <CircularProgress size={16} /> : 'Publish'}
              </Button>
              <Button
                variant="text"
                onClick={() => onSubmit(form, 'draft')}
                disabled={submitting}
              >
                Save as draft
              </Button>
            </>
          ) : (
            <Button
              variant="contained"
              onClick={() => onSubmit(form)}
              disabled={submitting}
            >
              {submitting ? <CircularProgress size={16} /> : 'Save Changes'}
            </Button>
          )}
        </Box>
      </Paper>
    </Box>
  );
};

export default ProjectForm;


